import Alert, { AlertColor } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ToastOptions = {
  message: ReactNode;
  severity?: AlertColor;
  duration?: number;
  title?: ReactNode;
};

type ToastApi = {
  show: (options: ToastOptions) => void;
  success: (message: ReactNode, title?: ReactNode) => void;
  error: (message: ReactNode, title?: ReactNode) => void;
  info: (message: ReactNode, title?: ReactNode) => void;
  warning: (message: ReactNode, title?: ReactNode) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

let externalApi: ToastApi | null = null;

/** Imperative helpers for use outside React components (e.g. service callbacks). */
export const toast = {
  show: (options: ToastOptions) => externalApi?.show(options),
  success: (message: ReactNode, title?: ReactNode) =>
    externalApi?.success(message, title),
  error: (message: ReactNode, title?: ReactNode) =>
    externalApi?.error(message, title),
  info: (message: ReactNode, title?: ReactNode) =>
    externalApi?.info(message, title),
  warning: (message: ReactNode, title?: ReactNode) =>
    externalApi?.warning(message, title),
};

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ToastOptions>({
    message: "",
    severity: "info",
    duration: 4000,
  });

  const show = useCallback((next: ToastOptions) => {
    setOptions({
      severity: "info",
      duration: 4000,
      ...next,
    });
    setOpen(true);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, title) =>
        show({ message, title, severity: "success" }),
      error: (message, title) =>
        show({ message, title, severity: "error", duration: 6000 }),
      info: (message, title) => show({ message, title, severity: "info" }),
      warning: (message, title) =>
        show({ message, title, severity: "warning" }),
    }),
    [show]
  );

  externalApi = api;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={options.duration}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={options.severity}
          variant="filled"
          sx={{
            width: "100%",
            alignItems: "center",
            boxShadow: "0 8px 24px rgba(27, 30, 51, 0.16)",
          }}
        >
          {options.title != null && (
            <strong style={{ display: "block", marginBottom: 2 }}>
              {options.title}
            </strong>
          )}
          {options.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}
